import {
  DOCUMENT_CATEGORY_OPTIONS,
  DOCUMENT_SOURCE_OPTIONS,
  type DocumentCategory,
  type DocumentSource,
} from '@seen/core/models/document-references/constants'
import { DocumentReviewTask } from '@seen/core/models/tasks/standard/documentReviewTask'
import type { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router'
import { VirtualizedList } from '#src/components/core/VirtualizedList.tsx'
import {
  FilterBar,
  FilterChip,
  type FilterChipOption,
} from '#src/components/filter-chip/index.ts'
import { IconTaskState } from '#src/components/icons/IconTaskState.tsx'
import { InboxDetailLayout } from '#src/components/layouts/InboxDetailLayout.tsx'
import { TaskPriorityIcon } from '#src/components/task/TaskPriorityIcon.tsx'
import { useHotkeys } from '#src/hooks/useHotkeys.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearchResourcePages } from '#src/hooks/useSearchResourcePages.ts'
import { DocumentInboxDetail } from '#src/pages/document-inbox/DocumentInboxDetail.tsx'
import { DocumentInboxTaskRow } from '#src/pages/document-inbox/DocumentInboxTaskRow.tsx'

type TaskState = StandardTask['state']

const CATEGORY_FILTER_OPTIONS: FilterChipOption<DocumentCategory>[] =
  DOCUMENT_CATEGORY_OPTIONS.map((opt) => ({
    value: opt.value,
    label: opt.label,
    keywords: [opt.label.toLowerCase()],
  }))

const SOURCE_FILTER_OPTIONS: FilterChipOption<DocumentSource>[] =
  DOCUMENT_SOURCE_OPTIONS.map((opt) => ({
    value: opt.value,
    label: opt.label,
    keywords: [opt.label.toLowerCase()],
  }))

type TaskPriority = 'none' | 'stat' | 'urgent' | 'asap' | 'routine'

const PRIORITY_FILTER_OPTIONS: FilterChipOption<TaskPriority>[] = [
  {
    value: 'none',
    label: (
      <FilterOption
        icon={<TaskPriorityIcon priority={undefined} />}
        text="No Priority"
      />
    ),
    keywords: ['none', 'no priority'],
  },
  {
    value: 'stat',
    label: (
      <FilterOption icon={<TaskPriorityIcon priority="stat" />} text="STAT" />
    ),
    keywords: ['stat', 'urgent'],
  },
  {
    value: 'urgent',
    label: (
      <FilterOption icon={<TaskPriorityIcon priority="urgent" />} text="High" />
    ),
    keywords: ['high'],
  },
  {
    value: 'asap',
    label: (
      <FilterOption icon={<TaskPriorityIcon priority="asap" />} text="Medium" />
    ),
    keywords: ['medium'],
  },
  {
    value: 'routine',
    label: (
      <FilterOption icon={<TaskPriorityIcon priority="routine" />} text="Low" />
    ),
    keywords: ['low'],
  },
]

const STATUS_FILTER_OPTIONS: FilterChipOption<TaskState>[] = [
  {
    value: 'todo',
    label: (
      <FilterOption icon={<IconTaskState state="todo" />} text="Not Started" />
    ),
    keywords: ['todo', 'not started'],
  },
  {
    value: 'in-progress',
    label: (
      <FilterOption
        icon={<IconTaskState state="in-progress" />}
        text="In Progress"
      />
    ),
    keywords: ['progress'],
  },
  {
    value: 'done',
    label: <FilterOption icon={<IconTaskState state="done" />} text="Done" />,
    keywords: ['done', 'completed'],
  },
  {
    value: 'backlog',
    label: (
      <FilterOption icon={<IconTaskState state="backlog" />} text="Backlog" />
    ),
    keywords: ['backlog'],
  },
  {
    value: 'canceled',
    label: (
      <FilterOption icon={<IconTaskState state="canceled" />} text="Canceled" />
    ),
    keywords: ['canceled'],
  },
  {
    value: 'duplicate',
    label: (
      <FilterOption
        icon={<IconTaskState state="duplicate" />}
        text="Duplicate"
      />
    ),
    keywords: ['duplicate'],
  },
]

function FilterOption({
  icon,
  text,
}: {
  icon: ReactNode
  text: string
}): ReactNode {
  return (
    <span className="flex items-center gap-2">
      {icon}
      {text}
    </span>
  )
}

function parseArrayParam<T extends string>(
  value: string | null,
  defaultValue: T[]
): T[] {
  if (value === null) {
    return defaultValue
  }
  if (value === '') {
    return []
  }
  return value.split(',').filter(Boolean) as T[]
}

export function DocumentInboxContent(): ReactNode {
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedTaskId = searchParams.get('task')

  const statusFilter = parseArrayParam<TaskState>(searchParams.get('status'), [
    'todo',
  ])
  const categoryFilter = parseArrayParam<DocumentCategory>(
    searchParams.get('category'),
    []
  )
  const sourceFilter = parseArrayParam<DocumentSource>(
    searchParams.get('source'),
    []
  )
  const priorityFilter = parseArrayParam<TaskPriority>(
    searchParams.get('priority'),
    []
  )

  const updateFilter = useCallback(
    (key: string, values: string[], clearTask = true) => {
      setSearchParams((prev) => {
        if (values.length === 0) {
          prev.delete(key)
        } else {
          prev.set(key, values.join(','))
        }
        if (clearTask) {
          prev.delete('task')
        }
        return prev
      })
    },
    [setSearchParams]
  )

  const setStatusFilter = useCallback(
    (values: TaskState[]) => updateFilter('status', values),
    [updateFilter]
  )
  const setCategoryFilter = useCallback(
    (values: DocumentCategory[]) => updateFilter('category', values),
    [updateFilter]
  )
  const setSourceFilter = useCallback(
    (values: DocumentSource[]) => updateFilter('source', values),
    [updateFilter]
  )
  const setPriorityFilter = useCallback(
    (values: TaskPriority[]) => updateFilter('priority', values),
    [updateFilter]
  )

  const hasActiveFilters =
    categoryFilter.length > 0 ||
    sourceFilter.length > 0 ||
    priorityFilter.length > 0 ||
    (statusFilter.length > 0 &&
      !(statusFilter.length === 1 && statusFilter[0] === 'todo'))

  const handleClearAllFilters = useCallback(() => {
    setSearchParams((prev) => {
      prev.set('status', 'todo')
      prev.delete('category')
      prev.delete('source')
      prev.delete('priority')
      prev.delete('task')
      return prev
    })
  }, [setSearchParams])

  const query = useMemo(
    () =>
      DocumentReviewTask.faxInboxQuery({
        state: statusFilter.length > 0 ? statusFilter : undefined,
        priority: priorityFilter.length > 0 ? priorityFilter : undefined,
        category: categoryFilter.length > 0 ? categoryFilter : undefined,
        source: sourceFilter.length > 0 ? sourceFilter : undefined,
      }),
    [statusFilter, priorityFilter, categoryFilter, sourceFilter]
  )

  const { data: allTasks = [] } = useSearchResourcePages(
    DocumentReviewTask,
    query
  )

  const sortedTasks = useMemo(() => {
    return [...allTasks].sort((a, b) =>
      b.authoredOn.localeCompare(a.authoredOn)
    )
  }, [allTasks])

  // Fetch the selected task directly by ID to support deep-links
  const { data: fetchedTask } = useRead(DocumentReviewTask, selectedTaskId)

  // Keep showing previous task while loading to prevent panel flicker
  const lastTaskRef = useRef<DocumentReviewTask | undefined>(undefined)
  useEffect(() => {
    if (fetchedTask) {
      lastTaskRef.current = fetchedTask
    }
  }, [fetchedTask])

  const selectedTask =
    fetchedTask ?? (selectedTaskId ? lastTaskRef.current : undefined)

  const selectTask = useCallback(
    (taskId: string | undefined) => {
      setSearchParams((prev) => {
        if (taskId) {
          prev.set('task', taskId)
        } else {
          prev.delete('task')
        }
        return prev
      })
    },
    [setSearchParams]
  )

  const advanceToNextTask = useCallback(() => {
    const currentIndex = sortedTasks.findIndex(
      (task) => task.id === selectedTaskId
    )

    if (currentIndex !== -1) {
      const nextIndex = currentIndex + 1
      if (nextIndex < sortedTasks.length) {
        selectTask(sortedTasks[nextIndex].id)
      } else {
        selectTask(undefined)
      }
    }
  }, [sortedTasks, selectedTaskId, selectTask])

  const handleTaskUpdate = useCallback(() => {}, [])

  useHotkeys(
    'j,ArrowDown',
    (e) => {
      e.preventDefault()
      const currentIndex = sortedTasks.findIndex(
        (task) => task.id === selectedTaskId
      )
      const nextIndex = currentIndex + 1
      if (nextIndex < sortedTasks.length) {
        selectTask(sortedTasks[nextIndex].id)
      }
    },
    [sortedTasks, selectedTaskId, selectTask]
  )

  useHotkeys(
    'k,ArrowUp',
    (e) => {
      e.preventDefault()
      const currentIndex = sortedTasks.findIndex(
        (task) => task.id === selectedTaskId
      )
      const prevIndex = currentIndex - 1
      if (prevIndex >= 0) {
        selectTask(sortedTasks[prevIndex].id)
      } else if (currentIndex === -1 && sortedTasks.length > 0) {
        selectTask(sortedTasks[0].id)
      }
    },
    [sortedTasks, selectedTaskId, selectTask]
  )

  const taskRows = useMemo(
    () =>
      sortedTasks.map((task) => (
        <DocumentInboxTaskRow
          key={task.id}
          task={task}
          isSelected={task.id === selectedTaskId}
          onSelect={() => selectTask(task.id)}
        />
      )),
    [sortedTasks, selectedTaskId, selectTask]
  )

  return (
    <InboxDetailLayout
      autoSaveId="fax-inbox-detail-layout"
      list={
        <>
          <div className="border-b bg-background p-3">
            <FilterBar
              hasActiveFilters={hasActiveFilters}
              onClearAll={handleClearAllFilters}
            >
              <FilterChip
                label="Status"
                options={STATUS_FILTER_OPTIONS}
                values={statusFilter}
                onChange={setStatusFilter}
                disableSearch
              />
              <FilterChip
                label="Category"
                options={CATEGORY_FILTER_OPTIONS}
                values={categoryFilter}
                onChange={setCategoryFilter}
              />
              <FilterChip
                label="Source"
                options={SOURCE_FILTER_OPTIONS}
                values={sourceFilter}
                onChange={setSourceFilter}
              />
              <FilterChip
                label="Priority"
                options={PRIORITY_FILTER_OPTIONS}
                values={priorityFilter}
                onChange={setPriorityFilter}
              />
            </FilterBar>
          </div>
          <div className="flex h-full flex-col overflow-hidden">
            <VirtualizedList
              rows={taskRows}
              estimateSize={() => 102}
              scrollRestorationId="fax-inbox-tasks"
            />
          </div>
        </>
      }
      detail={
        selectedTask ? (
          <DocumentInboxDetail
            task={selectedTask}
            onClose={() => selectTask(undefined)}
            onUpdate={handleTaskUpdate}
            onComplete={advanceToNextTask}
          />
        ) : null
      }
    />
  )
}
